import {
  Box,
  Button,
  SideNavigation,
  SpaceBetween,
  Toggle,
  type SideNavigationProps,
} from "@cloudscape-design/components";
import type { ThemeMode } from "@/core/ui/theme";

interface Props {
  active: "reports" | "inbox" | "rcm" | "profile" | "activity" | "how-it-works";
  openCount: number | null;
  /** Count of reports not in lifecycle `complete` — drives the Reports badge (#70). */
  openReportsCount: number | null;
  theme: ThemeMode;
  onToggleTheme: () => void;
  onNavigate: (target: Props["active"]) => void;
  onSignOut: () => void;
}

const HREF_TO_TARGET: Record<string, Props["active"]> = {
  "/inbox": "inbox",
  "/reports": "reports",
  "/rcm": "rcm",
  "/profile": "profile",
  "/activity": "activity",
  "/how-it-works": "how-it-works",
};

export function SideNav({
  active,
  openCount,
  openReportsCount,
  theme,
  onToggleTheme,
  onNavigate,
  onSignOut,
}: Props) {
  const onFollow = (e: CustomEvent<SideNavigationProps.FollowDetail>) => {
    e.preventDefault();
    const target = HREF_TO_TARGET[e.detail.href];
    if (target) onNavigate(target);
  };
  const activeHref =
    active === "inbox"
      ? "/inbox"
      : active === "rcm"
        ? "/rcm"
        : active === "profile"
          ? "/profile"
          : active === "activity"
            ? "/activity"
            : active === "how-it-works"
              ? "/how-it-works"
              : "/reports";

  const inboxInfo: SideNavigationProps.Item = {
    type: "link",
    text: "Action items",
    href: "/inbox",
    info: openCount && openCount > 0 ? <Box variant="strong">{openCount}</Box> : undefined,
  };

  const reportsInfo: SideNavigationProps.Item = {
    type: "link",
    text: "Reports",
    href: "/reports",
    info:
      openReportsCount && openReportsCount > 0 ? (
        <Box variant="strong">{openReportsCount}</Box>
      ) : undefined,
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={{ padding: "16px 16px 8px 16px" }}>
        <div className="sigon-mark">
          <span className="sigon-mark-row">
            <img src="/brand/sigon-logo.png" alt="Sigon" />
            <span className="sigon-mark-brand">SIGON</span>
          </span>
          <span className="sigon-mark-product">SOC1.LAB</span>
        </div>
      </div>
      <div style={{ flex: 1, overflowY: "auto" }}>
        <SideNavigation
          header={{ text: "", href: "/" }}
          activeHref={activeHref}
          onFollow={onFollow}
          items={[
            inboxInfo,
            reportsInfo,
            { type: "link", text: "RCM library", href: "/rcm" },
            { type: "link", text: "Company profile", href: "/profile" },
            { type: "divider" },
            { type: "link", text: "How it works", href: "/how-it-works" },
          ]}
        />
      </div>
      <Box padding="s">
        <SpaceBetween size="xs">
          <Toggle checked={theme === "dark"} onChange={onToggleTheme}>
            Dark mode
          </Toggle>
          <Button variant="link" iconName="undo" onClick={onSignOut}>
            Sign out
          </Button>
        </SpaceBetween>
      </Box>
    </div>
  );
}
